import { Interval, endOfMonth, isWithinInterval, startOfDay, startOfMonth, sub } from 'date-fns'
import { ReactNode, useCallback, useMemo, useState } from 'react'

import { Dialog, DialogContent, DialogProps } from '@mui/material'

import { useGetEventsForTrackedGamesQuery } from '../../api/core'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../features/live-events/hooks/useLiveEventTagGroups'
import { LiveEventsCalendarTimelineType, TrackingEventTimelineItem } from '../../features/live-events/types/LiveEvents'
import { TrackedGame } from '../../features/live-events/types/TrackedGame'
import { GranularityValue } from '../../features/revenue-and-downloads/types/Filters'
import { getIntervalByGranularity } from '../../helpers/date'
import { DateRangeValue } from '../DateRangePicker/DateRangePicker'
import GRDialogTitle from '../GRDialogTitle/GRDialogTitle'
import { PerformanceChartV2 } from '../PerformanceChartV2/PerformanceChartV2'
import { PerformanceChartV2DataType } from '../PerformanceChartV2/PerformanceChartV2DataType'
import { YAxisConfig } from '../PerformanceChartV2/PerformanceChartV2Types'

export type TimelineChartVerticalMark<MarkData> = {
  timestamp: number | Date | string
  data?: MarkData
}

/**
 * Component that displays a dialog with game performance data
 */
export type GamePerformaceDialogData = {
  appId: number
  marketIso: string
  gameName: string
  highlightedTimestamp?: number
  initialYAxisLeftConfig?: YAxisConfig
  initialYAxisRightConfig?: YAxisConfig
  initialGranularity?: GranularityValue
  selectedInstanceDuration?: Interval
  highlightedPeriod?: { start: number; end: number }
}

type GamePerformanceDialogProps<VerticalMarkDataType extends TimelineChartVerticalMark<any>> = DialogProps &
  GamePerformaceDialogData & {
    verticalMarks?: VerticalMarkDataType[]
    highlightedVerticalMarks?: VerticalMarkDataType[]
    selectedVerticalMarks?: VerticalMarkDataType[]
    onChartClick?: (interval?: Interval) => void
    render?: (granularity: GranularityValue, dateRange?: DateRangeValue) => ReactNode
    children?: ReactNode
    selectedGame?: TrackedGame | undefined
    showNewEventsStrip?: boolean
  }

export const GamePerformanceDialog = <VerticalMarkDataType extends TimelineChartVerticalMark<any>>({
  open,
  onClose,
  appId,
  marketIso,
  gameName,
  verticalMarks,
  highlightedVerticalMarks,
  selectedVerticalMarks,
  initialYAxisLeftConfig = {
    dataType: PerformanceChartV2DataType.Revenue,
    excludedDataTypes: [PerformanceChartV2DataType.DAU, PerformanceChartV2DataType.MAU, PerformanceChartV2DataType.ARPDAU],
  },
  initialYAxisRightConfig = {
    dataType: PerformanceChartV2DataType.Downloads,
    excludedDataTypes: [PerformanceChartV2DataType.DAU, PerformanceChartV2DataType.MAU, PerformanceChartV2DataType.ARPDAU],
  },
  initialGranularity = GranularityValue.Day,
  onChartClick,
  render,
  children,
  selectedGame,
  selectedInstanceDuration,
  highlightedPeriod,
  showNewEventsStrip = false,
}: GamePerformanceDialogProps<VerticalMarkDataType>) => {
  const selectedDate = startOfDay(selectedVerticalMarks && selectedVerticalMarks.length > 0 ? selectedVerticalMarks[0].timestamp : new Date())
  const initialDateRange = useGamePerformanceDialogInitialDateRange({ selectedDate })
  const finalDateRange = selectedInstanceDuration
    ? { fromDate: new Date(selectedInstanceDuration.start), toDate: new Date(selectedInstanceDuration.end) }
    : initialDateRange
  const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(finalDateRange)
  const { newEvents, isFetching: newEventsLoading } = useNewEventsForDialog(showNewEventsStrip ? selectedGame : undefined, marketIso, dateRange)
  const [granularity, setGranularity] = useState(initialGranularity)
  const [yAxisLeftConfig, setYAxisLeftConfig] = useState<YAxisConfig>(initialYAxisLeftConfig)
  const [yAxisRightConfig, setYAxisRightConfig] = useState<YAxisConfig>(initialYAxisRightConfig)

  const handleDateRangeChange = useCallback((dateRange: DateRangeValue | undefined) => {
    setDateRange(dateRange)
  }, [])

  const handleGranularityChange = (granularity: GranularityValue) => {
    setGranularity(granularity)
  }

  const handleChartClick = (xValue?: number) => {
    if (xValue) {
      const { start, end } = getIntervalByGranularity(granularity, xValue)
      onChartClick?.({ start, end })
    } else {
      onChartClick?.()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth disableRestoreFocus>
      <GRDialogTitle onClose={onClose}>{gameName}</GRDialogTitle>
      <DialogContent dividers>
        <PerformanceChartV2
          dateRange={dateRange}
          granularity={granularity}
          appId={appId}
          marketIso={marketIso}
          onDateRangeChanged={handleDateRangeChange}
          onGranularityChanged={handleGranularityChange}
          verticalMarks={verticalMarks}
          highlightedVerticalMarks={highlightedVerticalMarks}
          selectedVerticalMarks={selectedVerticalMarks}
          highlightedPeriod={highlightedPeriod}
          yAxisLeftConfig={yAxisLeftConfig}
          onYAxisLeftConfigChanged={(value) => setYAxisLeftConfig(value)}
          yAxisRightConfig={yAxisRightConfig}
          onYAxisRightConfigChanged={(value) => setYAxisRightConfig(value)}
          onChartClick={handleChartClick}
          selectedGame={selectedGame}
          newEvents={newEvents}
          newEventsLoading={newEventsLoading}
        />
        {render?.(granularity, dateRange)}
      </DialogContent>
    </Dialog>
  )
}

const useNewEventsForDialog = (
  selectedGame: TrackedGame | undefined,
  marketIso: string,
  dateRange: DateRangeValue | undefined
): { newEvents: TrackingEventTimelineItem[]; isFetching: boolean } => {
  const liveEventTagGroupsMapByEventTypeId = useLiveEventTagGroupsMapByEventTypeId()
  const gameId = selectedGame?.game.id
  const startTimestamp = dateRange?.fromDate ? startOfMonth(dateRange.fromDate).getTime() : 0
  const endTimestamp = dateRange?.toDate ? endOfMonth(dateRange.toDate).getTime() : 0

  const { data, isFetching } = useGetEventsForTrackedGamesQuery(
    { gameIds: gameId ? [gameId] : [], startTimestamp, endTimestamp, marketIso },
    { skip: !gameId || !dateRange?.fromDate || !dateRange?.toDate }
  )

  const newEvents = useMemo((): TrackingEventTimelineItem[] => {
    if (!selectedGame || !data) return []
    const events = data.events[selectedGame.game.id] ?? []
    return events
      .filter((e) => e.highlighted && e.firstInstance)
      .map((event, index) => ({
        id: `${event.eventId}-${index}`,
        group: event.typeId,
        title: event.name,
        start_time: event.start,
        end_time: event.end || event.start,
        canMove: false,
        canResize: false,
        trackingEventId: event.eventId,
        trackedGame: selectedGame,
        trackingEvent: event,
        highlighted: false,
        firstMechanicChange: !!(event.mechanicChange && event.firstInstance),
        new: !!(event.highlighted && event.firstInstance),
        type: LiveEventsCalendarTimelineType.TrackingEvent,
        tagGroup: liveEventTagGroupsMapByEventTypeId[event.typeId],
      }))
  }, [selectedGame, data, liveEventTagGroupsMapByEventTypeId])

  return { newEvents, isFetching }
}

const useGamePerformanceDialogInitialDateRange = ({
  selectedDate,
  rangeLengthInMonths = 3,
}: {
  selectedDate: number | Date
  rangeLengthInMonths?: number
}): DateRangeValue => {
  const defaultFromDate = sub(startOfDay(new Date()), { months: rangeLengthInMonths })
  const defaultToDate = startOfDay(new Date())
  const isSelectedDateWithinDefaultInterval = isWithinInterval(selectedDate, { start: defaultFromDate, end: defaultToDate })
  if (isSelectedDateWithinDefaultInterval) {
    return { fromDate: defaultFromDate, toDate: defaultToDate }
  }

  // Center the selected date inside a window equal in length to the default range.
  // Using milliseconds avoids fractional-month truncation in date-fns `add`/`sub`,
  // which otherwise produced an asymmetric range (e.g. 2 months before vs. 1 month
  // after the selected date for `rangeLengthInMonths / 2 === 1.5`).
  const halfWindowMs = (defaultToDate.getTime() - defaultFromDate.getTime()) / 2
  const selectedTimestamp = startOfDay(selectedDate).getTime()

  return {
    fromDate: new Date(selectedTimestamp - halfWindowMs),
    toDate: new Date(selectedTimestamp + halfWindowMs),
  }
}
